import type {TFunction} from 'i18next';
import {AutomationNode, NodeType} from 'crqlar-react-common/dist/models/automation/automation';

import {recipientStepName} from './recipients';

/** Hand-typed ids, and any stored config no option matches. */
export const OTHER_TARGET = 'other';

export interface InviteOption {
    /** Built from what the option writes, so two steps writing the same config collapse into one. */
    value: string;
    /** Sentence-initial. */
    label: string;
    /** For the summary: "Invites {{target}} to …". */
    target: string;
    /** The run's own reservation or guest — whom the API invites when both id fields are empty. */
    runEntity: boolean;
    reservationId?: string;
    profileId?: string;
}

/** Each option writes one of `reservationId` / `profileId` and leaves the other empty, as the API reads them. */
export const buildInviteOptions = (upstreamNodes: AutomationNode[], t: TFunction): InviteOption[] => {
    const options: InviteOption[] = [];
    const seen = new Set<string>();
    const add = (option: InviteOption) => {
        if (seen.has(option.value)) return;
        seen.add(option.value);
        options.push(option);
    };

    upstreamNodes.forEach(node => {
        const step = recipientStepName(node);
        switch (node.type) {
            case NodeType.RESERVATION_CREATED:
            case NodeType.RESERVATION_STATUS_CHANGED:
                add({
                    value: `r:{{"${step}".id}}`,
                    label: t('The reservation from ‘{{step}}’', {step}),
                    target: t('the guest on the reservation from ‘{{step}}’', {step}),
                    runEntity: true,
                    reservationId: `{{"${step}".id}}`
                });
                break;
            case NodeType.AUDIENCE:
                // A fan-out run is one guest, so the step's own `id` is that guest's profile.
                add({
                    value: `p:{{"${step}".id}}`,
                    label: t('The guest of ‘{{step}}’', {step}),
                    target: t('the guest of ‘{{step}}’', {step}),
                    runEntity: true,
                    profileId: `{{"${step}".id}}`
                });
                break;
            case NodeType.SURVEY_SUBMITTED:
            case NodeType.SURVEY_REMINDER_DUE:
                add({
                    value: `p:{{"${step}".profileId}}`,
                    label: t('The guest of ‘{{step}}’', {step}),
                    target: t('the guest of ‘{{step}}’', {step}),
                    runEntity: true,
                    profileId: `{{"${step}".profileId}}`
                });
                break;
            default:
                break;
        }
    });

    return options;
};

export interface InviteTarget {
    /** The option the stored pair matches, or — nothing stored — the run's own. */
    option?: InviteOption;
    /** Stored ids no option wrote (hand-typed, or a step that is gone). */
    custom: boolean;
    /** Nothing stored and no run entity to fall back to: the step would fail. */
    nobody: boolean;
    field: 'none' | 'select' | 'ids';
}

export const inviteTarget = (options: InviteOption[], config: Record<string, any>): InviteTarget => {
    const reservationId: string = config.reservationId || '';
    const profileId: string = config.profileId || '';
    const hasTarget = !!reservationId || !!profileId;

    // An option matches only when it wrote the whole stored pair.
    const known = options.find(option => option.reservationId
        ? option.reservationId === reservationId && !profileId
        : option.profileId === profileId && !reservationId);
    const option = hasTarget ? known : options.find(entry => entry.runEntity);
    const custom = hasTarget && !known;

    return {
        option,
        custom,
        nobody: !hasTarget && !option,
        field: options.length === 0 ? 'ids' : options.length === 1 && !custom ? 'none' : 'select',
    };
};
